import isMetaKey from '@uturn/portal/utils/is-meta-key';
import {
	type CustomCellEditorProps,
	MultiSelect,
	type MultiSelectOptionType,
} from '@uturn/ui-kit';
import { type FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Props = CustomCellEditorProps & {
	options: MultiSelectOptionType[];
};

/**
 * Known issues
 *
 * - The popover is not positioned properly, especially on Container Type this is noticable since it touches the border of the screen/goes off screen
 */
const MultiSelectEditor: FC<Props> = ({
	value,
	onValueChange,
	api,
	cellStartedEdit,
	rowIndex,
	column,
	eventKey,
	options,
}) => {
	const { t } = useTranslation();

	const searchRef = useRef<HTMLInputElement>(null);
	const [searchValue, setSearchValue] = useState(
		eventKey && !isMetaKey(eventKey) ? eventKey : '',
	);

	const [open, setOpen] = useState(cellStartedEdit);

	return (
		<MultiSelect
			selected={value}
			onChange={onValueChange}
			options={options}
			selectAllEnabled
			searchPlaceholder={t(
				'general.search.placeholder',
				'Search...',
			).toString()}
			emptyMessage={t('general.search.empty', 'No results found.').toString()}
			badgesDisabled={true}
			open={open}
			setOpen={(isOpen) => {
				setOpen(isOpen);

				if (isOpen) {
					return;
				}

				/**
				 * Whenever the popover is closed, we want to stop editing the cell, but doing so
				 * removes the focus from the cell. So after calling .stopEditing() we need to set
				 * the focus back to the cell.
				 */
				api.stopEditing();
				api.setFocusedCell(rowIndex, column.getColId());
			}}
			/**
			 * className "ag-custom-component-popup" is required for the popover to work
			 * in combination with ag-grid option stopEditingWhenCellsLoseFocus=true (see GridOptions).
			 */
			searchClassName="ag-custom-component-popup w-[300px]"
			searchRef={searchRef}
			searchValue={searchValue}
			searchOnValueChange={setSearchValue}
			searchOnSelect={(params) => {
				/**
				 * params.nativeEvent has a global "Event" type, which can be MouseEvent or KeyboardEvent
				 * that's why we check if key exists on the event to check for the meta key after.
				 *
				 * The reason we do the following, is because we can't overwrite the select behavior
				 * and don't want to actually select the value in the input, unless the user selects
				 * using a meta key (e.g. ctrl + a, ctrl + <, etc).
				 */
				if (
					'key' in params.nativeEvent &&
					isMetaKey(params.nativeEvent.key as string)
				) {
					return;
				}
				searchRef?.current?.setSelectionRange(
					searchValue.length,
					searchValue.length,
				);
			}}
		/>
	);
};

export default MultiSelectEditor;
